import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import {
  getAdminLuckyLottoHistory,
  LuckyLottoDrawRecord,
  LuckyLottoWinner,
} from '../../../services/redpacketService';
import { fNumber } from '../../../../../utils/format-number';

function WinnerRow({ winner }: { winner: LuckyLottoWinner }) {
  return (
    <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
      <TableCell />
      <TableCell>{winner.walletAddress}</TableCell>
      <TableCell>{winner.randomNumber}</TableCell>
      <TableCell>{winner.rank}</TableCell>
      <TableCell>{fNumber(parseFloat(winner.prizeAmount))}</TableCell>
      <TableCell>{fNumber(parseFloat(winner.tradingVolume))}</TableCell>
      <TableCell>
        {winner.drawTime ? new Date(winner.drawTime).toLocaleString() : '-'}
      </TableCell>
    </TableRow>
  );
}

function DrawRecordRow({ record, t }: { record: LuckyLottoDrawRecord; t: (key: string) => string }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover sx={{ '& > *': { verticalAlign: 'top' } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            {record.drawId.slice(0, 8)}...
          </Typography>
        </TableCell>
        <TableCell>{record.drawDate}</TableCell>
        <TableCell>
          {record.drawTime ? new Date(record.drawTime).toLocaleString() : '-'}
        </TableCell>
        <TableCell>{fNumber(parseFloat(record.prizePerWinner))}</TableCell>
        <TableCell>{fNumber(parseFloat(record.totalPrizeAmount))}</TableCell>
        <TableCell>{record.winnerCount}</TableCell>
        <TableCell>{record.participantCount}</TableCell>
        <TableCell>
          <Typography variant="caption" sx={{ maxWidth: 120, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {record.luckyNumbers?.join(', ') || '-'}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={record.prizeDistributed ? t('module.redpacket.yes') : t('module.redpacket.no')}
            color={record.prizeDistributed ? 'success' : 'default'}
            size="small"
          />
        </TableCell>
        <TableCell>
          {record.distributedAt ? new Date(record.distributedAt).toLocaleString() : '-'}
        </TableCell>
        <TableCell>
          {record.createdAt ? new Date(record.createdAt).toLocaleString() : '-'}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('module.redpacket.winners')} ({record.winners?.length || 0})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>{t('module.redpacket.wallet')}</TableCell>
                    <TableCell>{t('module.redpacket.randomNumber')}</TableCell>
                    <TableCell>{t('module.redpacket.rank')}</TableCell>
                    <TableCell>{t('module.redpacket.prize')}</TableCell>
                    <TableCell>{t('module.redpacket.tradingVol')}</TableCell>
                    <TableCell>{t('module.redpacket.drawTime')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.winners?.map((w, i) => (
                    <WinnerRow key={`${w.userId}-${i}`} winner={w} />
                  ))}
                </TableBody>
              </Table>
              {record.transactionHashes?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('module.redpacket.transactionHashes')}
                  </Typography>
                  <Box sx={{ maxHeight: 120, overflow: 'auto' }}>
                    {record.transactionHashes.map((hash, i) => (
                      <Typography
                        key={i}
                        variant="caption"
                        sx={{ fontFamily: 'monospace', fontSize: 11, display: 'block' }}
                      >
                        {hash}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const LuckyLottoHistoryList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [data, setData] = useState<LuckyLottoDrawRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ page: 0, perPage: 20 });
  const [drawDate, setDrawDate] = useState<Dayjs | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const offset = pagination.page * pagination.perPage;
      const drawDateStr = drawDate ? drawDate.format('YYYY-MM-DD') : undefined;
      const result = await getAdminLuckyLottoHistory(
        pagination.perPage,
        offset,
        drawDateStr
      );
      setData(result.records ?? []);
      setTotal(result.totalCount ?? 0);
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.failedToGetData'), { type: 'error' });
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [pagination, drawDate, notify,translate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (_: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPagination((prev) => ({
      ...prev,
      perPage: parseInt(e.target.value, 10),
      page: 0,
    }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleReset = () => {
    setDrawDate(null);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  return (
    <PermissionGuard resource="redpacket" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.redpacket.luckyLottoHistory')}
          links={[
            { name: translate('module.redpacket.redPacketManagement'), href: `/${Resources.redpacket}` },
            { name: translate('module.redpacket.luckyLottoHistory') },
          ]}
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={4}>
                  <DatePicker
                    label={translate('module.redpacket.drawDate')}
                    value={drawDate}
                    onChange={(value) => setDrawDate(value)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Stack direction="row" spacing={2}>
                    <Button variant="contained" onClick={handleSearch}>
                      {translate('module.redpacket.search')}
                    </Button>
                    <Button variant="outlined" onClick={handleReset}>
                      {translate('module.redpacket.reset')}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell>{translate('module.redpacket.drawId')}</TableCell>
                  <TableCell>{translate('module.redpacket.drawDate')}</TableCell>
                  <TableCell>{translate('module.redpacket.drawTime')}</TableCell>
                  <TableCell>{translate('module.redpacket.prizePerWinner')}</TableCell>
                  <TableCell>{translate('module.redpacket.totalPrize')}</TableCell>
                  <TableCell>{translate('module.redpacket.winners')}</TableCell>
                  <TableCell>{translate('module.redpacket.participants')}</TableCell>
                  <TableCell>{translate('module.redpacket.luckyNumbers')}</TableCell>
                  <TableCell>{translate('module.redpacket.distributed')}</TableCell>
                  <TableCell>{translate('module.redpacket.distributedAt')}</TableCell>
                  <TableCell>{translate('module.redpacket.createdAtCol')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                      {translate('module.redpacket.loading')}
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                      {translate('module.redpacket.noRecords')}
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((record) => (
                    <DrawRecordRow key={record.drawId} record={record} t={translate} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={pagination.page}
            onPageChange={handlePageChange}
            rowsPerPage={pagination.perPage}
            onRowsPerPageChange={handlePerPageChange}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default LuckyLottoHistoryList;
